import React, { useEffect, useState, Fragment } from "react";

// Semantic UI React
import {
  Icon,
  Menu,
  Segment,
  Sidebar,
  Header,
  Accordion,
  Dimmer,
  Loader,
  List,
  Button,
  Checkbox,
  Divider,
  Modal,
} from "semantic-ui-react";

// Styling
import "semantic-ui-css/semantic.min.css";
import "./Style.css";

// Third Party
import useSWRImmutable from "swr/immutable";
import axios from "axios";
import localforage from "localforage";
import toast, { Toaster } from "react-hot-toast";

// ESRI
import Map from "@arcgis/core/Map";
import MapView from "@arcgis/core/views/MapView";
import Extent from "@arcgis/core/geometry/Extent";
import esriId from "@arcgis/core/identity/IdentityManager";
import Zoom from "@arcgis/core/widgets/Zoom";
import * as identify from "@arcgis/core/rest/identify";
import IdentifyParameters from "@arcgis/core/rest/support/IdentifyParameters";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer.js";
import Legend from "@arcgis/core/widgets/Legend";
import * as query from "@arcgis/core/rest/query.js";
import Query from "@arcgis/core/rest/support/Query.js";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils.js";

// Custom
import useResizeHandler from "../Shared/useResizeHandler";
import BasemapWidget from "./MapComponents/BasemapWidget";
import StaticLayerWidget from "./MapComponents/StaticLayerWidget";
import LayerWidget from "./MapComponents/LayerWidget";
import MapImageLayer from "@arcgis/core/layers/MapImageLayer";
import IdentifyWidget from "./MapComponents/IdentifyWidget";
import ZoomWidget from "./MapComponents/ZoomWidget";
import BookmarkWidget from "./MapComponents/BookmarkWidget";
// import SearchWidget from "./MapComponents/SearchWidgets";
import SearchWidget from "../Shared/components/SearchWidget";
import Tooltip from "./MapComponents/Tooltip";
import ContextMenuWidget from "./MapComponents/ContextMenuWidget";
import CoordinateWidget from "./MapComponents/CoordinateWidget";
import ToolDropdown from "./MapComponents/ToolDropdown";
import MeasurementWidget from "./MapComponents/MeasurementWidget";
import DrawWidget from "./MapComponents/DrawWidget";
import LocationWidget from "./MapComponents/LocationWidget";
import PrintServiceWidget from "./MapComponents/PrintServiceWidget";
import ExportParcelsModal from "./MapComponents/ExportParcelsModal";
import ShareParcelsModal from "./MapComponents/ShareParcelsModal";
import PhotoModal from "./MapComponents/PhotoModal";
import BufferModal from "./MapComponents/BufferModal";
import PropertyNotifier from "./MapComponents/PropertyNotifier";

import {
  parcelsTemplate,
  propertyClassLabel,
  subdivisionTemplate,
  ERUTemplate,
  PlatIndexTemplate,
  SectionCornerTemplate,
  DesMoinesCountyRoadTemplate,
  FloodIndexTemplate,
  GPSMonumentTemplate,
  VotingPrecinctTemplate,
  CemeteriesTemplate,
  GeneralElections2020Template,
  GeneralElections2024Template,
  SepticPermits,
  CountyZoningAndFloodPlainPermits,
} from "./templates";

// List of url substrings to check for
const LINK_VALUES = ["Link", "Image", "PDF", "File", "URL", "Photo"];

const fetcher = (url) => axios.get(url).then((res) => res.data);
// var block_history = false;
var default_extent = new Extent({
  xmin: 2170244.54269575,
  ymin: 257059.937828542,
  xmax: 2421112.5982513,
  ymax: 408622.43782854,
  spatialReference: {
    wkid: 3418,
  },
});

function GISMappingApp() {
  const { height, width } = useResizeHandler();
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(null);
  // const [zoom, setZoom] = useState(null);
  const [sidebarVisibility, setSidebarVisibility] = useState(false);
  // const [history, setHistory] = useState(false);
  const [initialExtent, setInitialExtent] = useState(null);

  const [selectedParcel, setSelectedParcel] = useState(null);
  const [visibleLayers, setVisibleLayers] = useState([]);
  const [selectedFeatures, setSelectedFeatures] = useState(null);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [activeTool, setActiveTool] = useState({ tool: "Identify" });
  const [identifyGeometry, setIdentifyGeometry] = useState(null);

  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipMessage, setTooltipMessage] = useState(null);
  const [mapButtonsVisible, setMapButtonVisibility] = useState(true);

  // Modals
  const [bufferModalOpen, setBufferModalOpen] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [propertyNotifierModalOpen, setPropertyNotifierModalOpen] =
    useState(false);

  // const [degrees, setDegrees] = useState(false);
  // const [pictometryExplorer, setPictometryExplorer] = useState(false);

  const [localConfig, setLocalConfig] = useState(null);

  // Fetch the configs to buidl the app
  const {
    isLoading,
    data: config,
    error,
  } = useSWRImmutable("/apps/gma/build", fetcher);

  // console.warn({ config, view, height, width });

  useEffect(() => {
    const header_height = document.getElementById("site-header").offsetHeight;
    document.getElementById("map-container").style.height = `${
      height - header_height
    }px`;
  }, [height]);

  useEffect(() => {
    if (config) {
      // Add Localforage Store
      const store = localforage.createInstance({ name: "gma" });

      // console.log({ config });

      store
        .getItem("settings")
        .then((value) => {
          // we got our value
          // console.log({ value, config });

          // check if the localstorage object has the correct properties
          if (
            value &&
            value.hasOwnProperty("basemap") &&
            value.hasOwnProperty("identify_layers") &&
            value.hasOwnProperty("static_layers")
          ) {
            setLocalConfig(value);
          } else {
            setLocalConfig(config.default);
          }
          initialCheck();
        })
        .catch((err) => {
          // we got an error
          console.log(err);
          setLocalConfig(config.default);
          initialCheck();
        });
    }
  }, [config]);

  useEffect(() => {
    if (localConfig) {
      // console.error("localConfig useEffect", { localConfig });

      // Add Localforage Store
      const store = localforage.createInstance({ name: "gma" });

      store.setItem("settings", localConfig);
    }
  }, [localConfig]);

  const initialCheck = () => {
    // Load ESRI's IdentifyManager and add the token
    esriId.registerToken({
      server: config.config.server_url,
      token: config.config.token,
    });

    // Get the Parcel from the URL
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const parcelid = urlParams.get("parcelid");

    if (parcelid) {
      // create the Query
      const queryObj = new Query();
      queryObj.returnGeometry = true;
      queryObj.outFields = ["*"];
      queryObj.where = `PARCEL_NO = '${parcelid}'`;

      query
        .executeQueryJSON(config.config.parcels_url, queryObj)
        .then((result) => {
          if (result.features.length > 0) {
            const results = result.features.map((record) => {
              return {
                feature: record,
                layerName: "Parcels",
              };
            });

            setSelectedFeatures(results[0]);

            var ext = results[0].feature.geometry.extent.clone().expand(1.25);
            ext.spatialReference.wkid = 3418;

            setInitialExtent(ext);
          }
        });
    } else {
      setInitialExtent(default_extent);
    }
  };

  useEffect(() => {
    if (initialExtent) {
      // console.log("initialExtent UseEffect", {
      //   initialExtent,
      //   selectedFeatures,
      // });

      if (width > 600) {
        setSidebarVisibility(true);
      }

      let map = new Map();

      // Add the identify layers
      const ident_layers = new MapImageLayer({
        url: config.map.identify_service.value,
        config: config.map.identify_layers,
        title: "identify Layers",
        id: "identify_layers",
        print: true,
        legendEnabled: true,
      });
      map.layers.add(ident_layers);

      // Create the Graphics Layer
      const identifyTaskGraphicsLayer = new GraphicsLayer({
        id: "IdentifyTaskResults",
        print: true,
      });
      map.layers.add(identifyTaskGraphicsLayer);

      let mapView = new MapView({
        container: "map-container",
        map: map,
        extent: initialExtent,
        // ui: {
        //   components: [""],
        // },
        popup: {
          dockEnabled: true,
          dockOptions: {
            buttonEnabled: false,
            breakpoint: false,
          },
        },
        constraints: {
          rotationEnabled: false,
          minScale: 409600,
          maxScale: 200,
          minZoom: 0,
          maxZoom: 11,
          lods: [
            { level: 0, resolution: 355.55555555555554, scale: 409600 },
            { level: 1, resolution: 177.77777777777777, scale: 204800 },
            { level: 2, resolution: 88.88888888888889, scale: 102400 },
            { level: 3, resolution: 44.44444444444444, scale: 51200 },
            { level: 4, resolution: 22.22222222222222, scale: 25600 },
            { level: 5, resolution: 11.11111111111111, scale: 12800 },
            { level: 6, resolution: 5.555555555555555, scale: 6400 },
            { level: 7, resolution: 2.7777777777777777, scale: 3200 },
            { level: 8, resolution: 1.3888888888888888, scale: 1600 },
            { level: 9, resolution: 0.6944444444444444, scale: 800 },
            { level: 10, resolution: 0.3472222222222222, scale: 400 },
            { level: 11, resolution: 0.1736111111111111, scale: 200 },
          ],
        },
      });
      mapView.popup.popupEnabled = true;
      mapView.popup.dockEnabled = true;
      mapView.popup.highlightEnabled = false;

      mapView.when(() => {
        new Zoom({
          view: mapView,
        });

        mapView.popup.maxInlineActions = 4;

        new Legend({
          container: "layer_legend",
          view: mapView,
        });
      });

      ident_layers.when(() => {
        setView(mapView);
      });
    }
  }, [initialExtent]);

  useEffect(() => {
    if (view) {
      if (selectedFeatures) {
        setTimeout(() => {
          renderPopup([selectedFeatures]);
          setLoading(false);
        }, 1000);
      } else {
        setLoading(false);
      }

      reactiveUtils.watch(
        () => view.popup.visible,
        (visible) => {
          if (!visible) {
            setSelectedParcel(null);
          }

          // add a check for mobile devices and when the popup is active to hide the map buttons
          if (width < 550) {
            setMapButtonVisibility(visible ? false : true);
          } else {
            setMapButtonVisibility(true);
          }
        }
      );

      reactiveUtils.on(
        () => view.popup,
        "trigger-action",
        (event) => {
          // Execute the measureThis() function if the measure-this action is clicked
          if (event.action.id === "export-parcels") {
            setExportModalOpen(true);
          } else if (event.action.id === "share-parcel") {
            // console.log(view.popup.selectedFeature);
            setShareModalOpen(true);
            setSelectedFeature(view.popup.selectedFeature);
          } else if (event.action.id === "photo-parcel") {
            // console.log(view.popup.selectedFeature);
            setPhotoModalOpen(true);
            setSelectedFeature(view.popup.selectedFeature);
          } else if (event.action.id === "property-notifier") {
            // console.log(view.popup.selectedFeature);
            setPropertyNotifierModalOpen(true);
            setSelectedFeature(view.popup.selectedFeature);
          }
        }
      );

      reactiveUtils.watch(
        () => view.popup.selectedFeature,
        (graphic) => {
          if (graphic) {
            var selected_graphic = graphic.clone();

            view.graphics.removeAll();

            // check if the feautre is a parcel
            if (selected_graphic.attributes.layerName === "Parcels") {
              setSelectedParcel(selected_graphic);
            }

            if (selected_graphic.geometry.type == "polygon") {
              selected_graphic.symbol = {
                type: "simple-fill",
                outline: {
                  width: 3,
                  color: [255, 0, 0, 0.8],
                  cap: "round",
                  join: "round",
                },
                color: [26, 26, 26, 0.17],
              };
            } else if (selected_graphic.geometry.type == "point") {
              selected_graphic.symbol = {
                type: "simple-marker",
                outline: { width: 2, color: [255, 0, 0, 1] },
                size: 20,
                color: [255, 255, 255, 0],
              };
            } else if (selected_graphic.geometry.type == "polyline") {
              selected_graphic.symbol = {
                type: "simple-line",
                width: 4,
                color: [255, 0, 0, 1],
              };
            }
            view.graphics.add(selected_graphic);

            // Push the state to the URL
            if (
              window.history &&
              typeof window.history.pushState !== "undefined" &&
              selected_graphic.attributes.layerName == "Parcels" &&
              selected_graphic.attributes.PARCEL_NO !== "WT"
            ) {
              window.history.pushState(
                null,
                null,
                "?parcelid=" + selected_graphic.attributes.PARCEL_NO
              );
            }
          }
        }
      );
    }
  }, [view]);

  useEffect(() => {
    if (view && selectedFeatures) {
      // console.log({ selectedFeatures });

      let identify_graphic_layer = clearIdentifyGraphics();

      selectedFeatures.map((record) => {
        // console.log({ record: record });

        if (record.feature.geometry.type == "polygon") {
          record.feature.symbol = {
            type: "simple-fill",
            outline: {
              width: 3,
              color: [0, 0, 0, 0.8],
              cap: "round",
              join: "round",
            },
            color: [0, 0, 0, 0],
          };
        } else if (record.feature.geometry.type == "point") {
          record.feature.symbol = {
            type: "simple-marker",
            outline: { width: 2, color: [0, 0, 0, 1] },
            size: 20,
            color: [0, 0, 0, 0],
          };
        } else if (record.feature.geometry.type == "polyline") {
          record.feature.symbol = {
            type: "simple-line",
            width: 4,
            color: [0, 0, 0, 1],
          };
        }
        identify_graphic_layer.graphics.add(record.feature);
      });
    }
  }, [selectedFeatures]);

  useEffect(() => {
    if (identifyGeometry) {
      executeIdentifyTask(identifyGeometry);
    }
  }, [identifyGeometry]);

  useEffect(() => {
    if (view) {
      if (sidebarVisibility) {
        view.padding = {
          top: view.padding.top,
          right: 350,
          bottom: view.padding.bottom,
          left: view.padding.left,
        };
      } else {
        view.padding = {
          top: view.padding.top,
          right: 0,
          bottom: view.padding.bottom,
          left: view.padding.left,
        };
      }
    }
  }, [view, sidebarVisibility]);

  const clearIdentifyGraphics = () => {
    let identify_graphic_layer = view.map.findLayerById("IdentifyTaskResults");
    identify_graphic_layer.graphics.removeAll();
    return identify_graphic_layer;
  };

  const executeIdentifyTask = (geometry) => {
    // console.error("executeIdentifyTask", {
    //   geometry,
    //   view,
    //   config,
    //   visibleLayers,
    //   sidebarVisibility,
    // });

    // skip if no visible layers found
    if (visibleLayers.length === 0) {
      return;
    }

    // Set the parameters for the Identify
    var identifyParams = new IdentifyParameters();
    identifyParams.tolerance = 10;
    identifyParams.returnGeometry = true;
    identifyParams.layerOption = "visible";
    identifyParams.width = view.width;
    identifyParams.height = view.height;
    identifyParams.layerIds = visibleLayers;
    identifyParams.geometry = geometry;
    identifyParams.mapExtent = view.extent;
    identifyParams.returnFieldName = false;

    // Identify the service
    identify
      .identify(config.map.identify_service.value, identifyParams)
      .then((response) => {
        // console.error("Identify Response: ", {
        //   response,
        // });
        setIdentifyGeometry(null);

        // Always return the Parcels First
        const results = response.results.reduce((acc, element) => {
          if (element.layerName == "Parcels") {
            return [element, ...acc];
          }
          return [...acc, element];
        }, []);

        if (results.length > 0) {
          renderPopup(results);
        } else {
          clearIdentify();
        }
      });
  };

  const clearIdentify = () => {
    // console.log("CLEAR IDENTIFY");
    clearIdentifyGraphics();
    view.graphics.removeAll();
    view.closePopup();
    setSelectedFeatures(null);
  };

  const zoomToExtent = (feature) => {
    // expand the extent and override the spatial reference because esri sucks
    var ext = feature.geometry.extent.clone().expand(1.25);
    ext.spatialReference.wkid = 3418;

    // Zoom to the extent
    view.goTo(ext);
  };

  const renderPopup = (results) => {
    setSelectedFeatures(results);

    const promises = results.map((result) => {
      return new Promise((resolve, reject) => {
        var feature = result.feature;
        var layerName = result.layerName;

        // Set the Layer Name
        feature.attributes.layerName = layerName;

        console.log(layerName, feature);

        if (layerName == "Parcels") {
          // block_history = false;
          // Get the property class label
          feature.attributes.PropertyClass = propertyClassLabel(
            feature.attributes.PropertyCl
          );

          feature.popupTemplate = {
            title: "Parcels ({PARCEL_NO})",
            content: parcelsTemplate({
              render_auditor: config.setting.render_auditor,
              current_tax_year: config.setting.current_tax_year,
              auditor_tax_year: config.setting.auditor_tax_year,
              past_tax_year: config.setting.past_tax_year,
              domain: config.config.domain,
            }),
            actions: [
              {
                title: "Export",
                id: "export-parcels",
                className: `esri-icon-table`,
              },
              {
                title: "Share",
                id: "share-parcel",
                className: `esri-icon-share`,
              },
              {
                title: "Photos",
                id: "photo-parcel",
                className: `esri-icon-media`,
              },
              {
                title: "Notifications",
                id: "property-notifier",
                className: `esri-icon-contact`,
              },
            ],
          };
        } else if (layerName == "ERU Fee Assignment") {
          feature.popupTemplate = {
            title: layerName,
            content: ERUTemplate(),
          };
        } else if (layerName == "Plat Index") {
          feature.popupTemplate = {
            title: layerName,
            content: PlatIndexTemplate(),
          };
        } else if (layerName == "Section Corners") {
          feature.popupTemplate = {
            title: layerName,
            content: SectionCornerTemplate(),
          };
        } else if (layerName == "DMC_Des Moines County Roads") {
          feature.popupTemplate = {
            title: layerName,
            content: DesMoinesCountyRoadTemplate(),
          };
        } else if (layerName == "Flood Index") {
          feature.popupTemplate = {
            title: layerName,
            content: FloodIndexTemplate(),
          };
        } else if (layerName == "GPS Monuments") {
          feature.popupTemplate = {
            title: layerName,
            content: GPSMonumentTemplate(),
          };
        } else if (layerName == "Voting Precincts") {
          feature.popupTemplate = {
            title: layerName,
            content: VotingPrecinctTemplate(),
          };
        } else if (layerName == "Cemetery") {
          feature.popupTemplate = {
            title: layerName,
            content: CemeteriesTemplate(),
          };
        } else if (layerName == "Subdivisions") {
          feature.popupTemplate = {
            title: layerName,
            content: subdivisionTemplate(
              feature.attributes.hasOwnProperty("Image") &&
                feature.attributes["Image"] != "0"
            ),
          };
        } else if (layerName == "2020 General Election Results") {
          feature.popupTemplate = {
            title: layerName,
            content: GeneralElections2020Template(feature.attributes),
          };
        } else if (layerName == "General 2024 Election Results") {
          feature.popupTemplate = {
            title: layerName,
            content: GeneralElections2024Template(feature.attributes),
          };
        } else if (layerName == "SepticPermits") {
          feature.popupTemplate = {
            title: "Septic Permits",
            content: SepticPermits(feature.attributes),
          };
        } else if (layerName == "County Zoning and Floodplain Permits") {
          feature.popupTemplate = {
            title: "County Zoning and Floodplain Permits",
            content: CountyZoningAndFloodPlainPermits(feature.attributes),
          };
        } else {
          feature.popupTemplate = {
            title: layerName,
            content: () => {
              var html = `<table class="esri-widget__table"><tbody>`;
              for (var key in feature.attributes) {
                // Check if the field name includes an indicator for a link
                // LINK_VALUES
                let link_value_match = key
                  .split(" ")
                  .find((term) => LINK_VALUES.includes(term));

                // Skip if the key is in the blacklist
                if (
                  ["id", "fid", "shape", "layername"].includes(
                    key.toLowerCase()
                  )
                ) {
                  continue;
                }

                // Add it as a new row in the table
                html += `<tr>`;
                html += `<td class="third">${key}</td>`;

                if (
                  link_value_match &&
                  feature.attributes[key].includes("http")
                ) {
                  html += `<td class="two-third">
                    <a class="ui fluid button" href="${feature.attributes[key]}" role="button">
                      <i aria-hidden="true" class="external icon"></i>
                      View ${link_value_match}
                    </a>
                  </td>`;
                } else {
                  html += `<td class="two-third">${
                    feature.attributes[key]
                    // ? feature.attributes[key]
                    // : "Not Available"
                  }</td>`;
                }
                html += `</tr>`;
              }
              html += `</tbody></table>`;
              return html;
            },
          };
        }

        resolve(feature);
      });
    });

    Promise.all(promises).then((features) => {
      if (features.length > 0) {
        view.openPopup({
          features: features,
        });
      }
    });
  };

  // const activateTool = (tool, arg) => {
  //   console.log("activateTool", { tool });
  //   setActiveTool(tool);
  // };

  const activateTool = (obj) => {
    // console.log("activateTool", obj, activeTool);

    // Check what the current tool is and deactivate it (if set)
    if (typeof obj !== "undefined" && obj.hasOwnProperty("tool")) {
      deactivateTool(activeTool);

      // Find the selected tool and set it
      // => Default to identify if need
      switch (obj.tool) {
        case "Identify":
          // Set the default identifier
          // this.renderIdentifyWidget(true);
          break;
        case "Measurement":
          // this.measurementHandler.start(obj.type);
          break;
        case "Graphic":
          //   // this.renderSketch();
          //   // this.renderDrawWidget();
          break;
        default:
          console.error(`UNKNOWN TOOL TYPE. COULD NOT ACTIVATE: ${obj.tool}`);
      }

      setActiveTool(obj);
    }
  };

  // Deactivates the current tool
  const deactivateTool = () => {
    // console.error("deactivateTool", activeTool);

    // Deactivates the current activeTool
    if (typeof activeTool !== "undefined") {
      switch (activeTool.tool) {
        case "Identify":
          clearIdentify();
          break;
        // default:
        //   console.error(
        //     `UNKNOWN TOOL TYPE. COULD NOT DEACTIVATE: ${this.state.activeTool}`
        //   );
      }
    }

    setActiveTool(null);
  };

  if (isLoading) {
    <Segment style={{ width: "100%", height: "100%" }}>
      <Dimmer active>
        <Loader>Building Map</Loader>
      </Dimmer>
    </Segment>;
  }

  return (
    <Fragment>
      {bufferModalOpen && (
        <BufferModal
          setOpen={setBufferModalOpen}
          graphic={selectedParcel}
          setIdentifyGeometry={setIdentifyGeometry}
          view={view}
        />
      )}

      {exportModalOpen && (
        <ExportParcelsModal
          selectedFeatures={selectedFeatures.filter(
            (record) => record.layerName === "Parcels"
          )}
          open={exportModalOpen}
          setOpen={setExportModalOpen}
          configs={config.urls}
        />
      )}

      {shareModalOpen && (
        <ShareParcelsModal
          selectedFeature={selectedFeature}
          open={shareModalOpen}
          setOpen={setShareModalOpen}
          domain={config.config.domain}
        />
      )}

      {photoModalOpen && (
        <PhotoModal
          selectedFeature={selectedFeature}
          open={photoModalOpen}
          setOpen={setPhotoModalOpen}
          domain={config.config.domain}
          token={config.urls.token}
        />
      )}

      {propertyNotifierModalOpen && (
        <PropertyNotifier
          selectedFeature={selectedFeature}
          open={propertyNotifierModalOpen}
          setOpen={setPropertyNotifierModalOpen}
          domain={config.config.domain}
          token={config.urls.token}
        />
      )}

      <Menu id="site-header" borderless attached="top" inverted icon>
        <Menu.Item header>Des Moines County GIS Mapping Application</Menu.Item>
        <Menu.Menu position="right">
          <Menu.Item onClick={() => setSidebarVisibility(!sidebarVisibility)}>
            <Icon name="bars" />
          </Menu.Item>
        </Menu.Menu>
      </Menu>
      <Sidebar.Pushable as={Segment} id="site-pushable" attached>
        {loading && (
          <Dimmer active>
            <Loader>Building Map</Loader>
          </Dimmer>
        )}
        {view && config && (
          <SiteSidebar
            view={view}
            config={config}
            sidebarVisibility={sidebarVisibility}
            setSidebarVisibility={setSidebarVisibility}
            setVisibleLayers={setVisibleLayers}
            renderPopup={renderPopup}
            zoomToExtent={zoomToExtent}
            localConfig={localConfig}
            setLocalConfig={setLocalConfig}
            width={width}
          />
        )}
        <Sidebar.Pusher id="site-pusher">
          <Segment id="site-container">
            {config && view && (
              <Fragment>
                <div
                  className={`top-left map-buttons ${
                    mapButtonsVisible ? "" : "hidden"
                  }`}
                >
                  <ZoomWidget view={view} />
                  <BookmarkWidget view={view} />
                  <LocationWidget view={view} />
                  <PrintServiceWidget
                    // url={this.props.configs.app.print_url}
                    view={view}
                    url={config.config.print_service_url}
                    localConfig={localConfig}
                    setLocalConfig={setLocalConfig}
                    // setTooltipMessage={setTooltipMessage}
                    // setTooltipVisible={setTooltipVisible}
                    // url={config.config.print_service_url}
                    // localConfig={localConfig}
                    // setLocalConfig={setLocalConfig}
                    // activeTool={activeTool}
                    // deactivateTool={deactivateTool}
                    // activateTool={activateTool}
                    // view={view}
                    // token={config.config.token}
                  />
                  <ToolDropdown
                    activeTool={activeTool}
                    activateTool={activateTool}
                  />

                  <IdentifyWidget
                    active={activeTool && activeTool.tool === "Identify"}
                    view={view}
                    setIdentifyGeometry={setIdentifyGeometry}
                    setTooltipMessage={setTooltipMessage}
                    setTooltipVisible={setTooltipVisible}
                    selectedParcel={selectedParcel}
                    setBufferModalOpen={setBufferModalOpen}
                  />
                </div>
                <div className="bottom-left map-buttons">
                  {activeTool && activeTool.tool === "Graphic" && (
                    <DrawWidget view={view} />
                  )}
                  <MeasurementWidget
                    view={view}
                    activeTool={activeTool}
                    parcels_service_url={config.config.parcels_url}
                  />
                </div>
                <CoordinateWidget
                  sidebarVisibility={sidebarVisibility}
                  view={view}
                  latlon={localConfig.render_lat_lon}
                />
                <Toaster
                  position="bottom-center"
                  toastOptions={{ duration: 3000 }}
                  containerStyle={{
                    marginRight: sidebarVisibility ? "350px" : "0px",
                  }}
                />
              </Fragment>
            )}
            <div id="map-container">
              {config && view && (
                <Fragment>
                  {tooltipVisible && (
                    <Tooltip
                      view={view}
                      message={tooltipMessage}
                      visible={tooltipVisible}
                    />
                  )}
                  <ContextMenuWidget
                    service={config.config.parcels_url}
                    google_street_view_api_key={
                      config.config.google_street_view_key
                    }
                    view={view}
                    render_explorer={localConfig.pictometry_explorer}
                    toast={toast}
                    localConfig={localConfig}
                    setLocalConfig={setLocalConfig}
                  />
                </Fragment>
              )}
            </div>
          </Segment>
        </Sidebar.Pusher>
      </Sidebar.Pushable>
    </Fragment>
  );
}

const SiteSidebar = ({
  view,
  config,
  setSidebarVisibility,
  sidebarVisibility,
  setVisibleLayers,
  renderPopup,
  zoomToExtent,
  width,
  localConfig,
  setLocalConfig,
}) => {
  const [visible, setVisible] = useState(sidebarVisibility);
  const [activeLayersAccordion, setActiveLayersAccordion] = useState(2);
  const [activeTab, setActiveTab] = useState("search");
  const [activeAccordion, setActiveAccordion] = useState(0);

  const searchCallback = (record) => {
    const queryObj = new Query();
    queryObj.returnGeometry = true;
    queryObj.outFields = ["*"];
    queryObj.where = `${record.field} = '${record.value}'`;

    query
      .executeQueryJSON(config.config.parcels_url, queryObj)
      .then(function (result) {
        console.log({ result });

        const features = result.features.map((feature) => {
          return {
            feature: feature,
            layerName: "Parcels",
          };
        });

        // zoom to extent
        zoomToExtent(features[0].feature);

        // render the popup
        renderPopup(features);

        // check the width
        if (width < 600) {
          setSidebarVisibility(false);
        }
      });
  };

  return (
    <Sidebar
      id="site-sidebar"
      as={Segment}
      animation="overlay"
      icon="labeled"
      direction="right"
      onHide={() => setVisible(visible)}
      vertical
      visible={sidebarVisibility}
      width="very wide"
    >
      <div id="sidebar-menu">
        <Menu fluid pointing secondary widths={4} className="no-margin">
          <Menu.Item
            name="Search"
            active={activeTab === "search"}
            onClick={() => setActiveTab("search")}
          />
          <Menu.Item
            name="Layers"
            active={activeTab === "layers"}
            onClick={() => setActiveTab("layers")}
          />
          <Menu.Item
            name="Legend"
            active={activeTab === "legend"}
            onClick={() => setActiveTab("legend")}
          />
          <Menu.Item
            name="Help"
            active={activeTab === "help"}
            onClick={() => setActiveTab("help")}
          />
        </Menu>
      </div>

      <div className="sidebar-content">
        <div style={{ display: activeTab === "search" ? "block" : "none" }}>
          <SearchWidget
            service={config.config.parcels_url}
            result_callback={searchCallback}
          />
        </div>

        <div style={{ display: activeTab === "layers" ? "block" : "none" }}>
          <Accordion style={{ padding: "0 .5em .5em" }}>
            <Accordion.Title
              active={activeLayersAccordion === 0}
              index={0}
              onClick={() =>
                setActiveLayersAccordion(activeLayersAccordion === 0 ? null : 0)
              }
            >
              <Icon name="dropdown" />
              Basemap
            </Accordion.Title>
            <Accordion.Content active={activeLayersAccordion === 0}>
              <BasemapWidget
                view={view}
                basemaps={config.map.basemaps}
                localConfig={localConfig}
                setLocalConfig={setLocalConfig}
              />
            </Accordion.Content>
            <Accordion.Title
              active={activeLayersAccordion === 1}
              index={1}
              onClick={() =>
                setActiveLayersAccordion(activeLayersAccordion === 1 ? null : 1)
              }
            >
              <Icon name="dropdown" />
              Static Layers
            </Accordion.Title>
            <Accordion.Content active={activeLayersAccordion === 1}>
              <StaticLayerWidget
                view={view}
                static_layers={config.map.static_layers}
                localConfig={localConfig}
                setLocalConfig={setLocalConfig}
              />
            </Accordion.Content>
            <Accordion.Title
              active={activeLayersAccordion === 2}
              index={2}
              onClick={() =>
                setActiveLayersAccordion(activeLayersAccordion === 2 ? null : 2)
              }
            >
              <Icon name="dropdown" />
              Identify Layers
            </Accordion.Title>
            <Accordion.Content active={activeLayersAccordion === 2}>
              {view && (
                <LayerWidget
                  view={view}
                  setVisibleLayers={setVisibleLayers}
                  localConfig={localConfig}
                  setLocalConfig={setLocalConfig}
                />
              )}
            </Accordion.Content>
          </Accordion>
        </div>
        <div style={{ display: activeTab === "legend" ? "block" : "none" }}>
          <div id="layer_legend"></div>
        </div>
        <div style={{ display: activeTab === "help" ? "block" : "none" }}>
          <Accordion styled>
            <Accordion.Title
              active={activeAccordion === 0}
              index={0}
              onClick={() => setActiveAccordion(0)}
            >
              <Icon name="dropdown" />
              Settings
            </Accordion.Title>
            <Accordion.Content active={activeAccordion === 0}>
              <Checkbox
                toggle
                label={<label>Render Pictometry Explorer</label>}
                defaultChecked={localConfig.pictometry_explorer}
                onClick={(e, { checked }) => {
                  // Update the localConfig
                  var obj = { ...localConfig };
                  obj.pictometry_explorer = checked;

                  setLocalConfig(obj);
                }}
              />
              <Divider />
              <Checkbox
                toggle
                label={<label>Render Degree Minute Seconds</label>}
                defaultChecked={localConfig.render_lat_lon}
                onClick={(e, { checked }) => {
                  // Update the localConfig
                  var obj = { ...localConfig };
                  obj.render_lat_lon = checked;

                  setLocalConfig(obj);
                }}
              />
            </Accordion.Content>

            <Accordion.Title
              active={activeAccordion === 1}
              index={1}
              onClick={() => setActiveAccordion(1)}
            >
              <Icon name="dropdown" />
              General Use
            </Accordion.Title>
            <Accordion.Content active={activeAccordion === 1}>
              <Header dividing as="h5">
                Basic Map Navigation
              </Header>

              <List bulleted>
                <List.Item>Click and Drag to pan</List.Item>
                <List.Item>SHIFT + Drag to zoom in</List.Item>
                <List.Item>SHIFT + CTRL + Drag to zoom out</List.Item>
                <List.Item>Mouse Scroll Forward to zoom in</List.Item>
                <List.Item>Mouse Scroll Backward to zoom out</List.Item>
                <List.Item>Double Click to Center and Zoom in</List.Item>
              </List>

              <p>
                Map Navigation tools are available in the top left to zoom in
                and out of the map. Below that is the Bookmark widget which will
                zoom to specific Areas of Interests quickly.
              </p>
              <Header dividing as="h5">
                Obtaining Layer Information
              </Header>
              <p>
                Layers can only be identified if they are visible on the map and
                visibility can be toggled under the Layers tab on the sidebar.
                By default clicking on the map will identify any features under
                the cursor. There is a dropdown on the left side of the screen
                that allows changing the identify type as Point, Extent,
                Polygon, and Polyline. Resulting features will be presented in a
                popup on the screen that can be iterated through across multiple
                layers.
              </p>
              <Header dividing as="h5">
                Obtaining Parcel Information
              </Header>
              <p>
                Parcels are integrated with the Layer Identification process.
                Parcel information can also be found by using the Search tool in
                the sidebar. This allows selecting by Parcel Number, Owner Name,
                and Address. The popup window has links to the Assessor and Iowa
                Treasurer website. There is also an option to export the Parcel
                as a PDF.
              </p>
            </Accordion.Content>

            <Accordion.Title
              active={activeAccordion === 2}
              index={2}
              onClick={() => setActiveAccordion(2)}
            >
              <Icon name="dropdown" />
              Basemaps, Layers, and Legend
            </Accordion.Title>
            <Accordion.Content active={activeAccordion === 2}>
              <Header dividing as="h5">
                Basemaps
              </Header>
              <p>
                Several historic basemaps are selectable. Tiled Layers are
                cached by the server to provide quicker response time. Dynamic
                layers are not cached and take a longer time to load from the
                server.
              </p>

              <Header dividing as="h5">
                Layers
              </Header>
              <p>
                Static layers are non-identifiable layers that display labels.
              </p>
              <p>
                Identifiable layers can be selected by clicking on a graphic to
                view attributable information in a popup window.
              </p>
              <p>
                All layers are remembered so that the layers turn on
                automatically the next time the website is accessed.
              </p>

              <Header dividing as="h5">
                Legend
              </Header>
              <p>
                Several historic basemaps are selectable. Tiled Layers are
                cached by the server to provide quicker response time. Dynamic
                layers are not cached and take a longer time to load from the
                server.
              </p>
            </Accordion.Content>

            <Accordion.Title
              active={activeAccordion === 3}
              index={3}
              onClick={() => setActiveAccordion(3)}
            >
              <Icon name="dropdown" />
              Tools
            </Accordion.Title>
            <Accordion.Content active={activeAccordion === 3}>
              <Header dividing as="h5">
                Measurement
              </Header>

              <p>
                Accessable via the Tools Dropdown. Allows measurement by Line,
                Area, Point, and by selecting a single parcel. Line and Area
                drawings are compelted on a double click. The resulting data
                will be rendered in the bottom right of the screen and allows
                the user to change the values.
              </p>
              <Header dividing as="h5">
                Graphics
              </Header>
              <p>
                Accessable via the Tools Dropdown. Allows the user to draw
                graphics on the map available from the options that render on
                the bottom right of the screen.
              </p>
              <Header dividing as="h5">
                Geolocation
              </Header>
              <p>
                Allows user's location to be tracked on the map. Requires User's
                device permission and User's location data is not stored in
                application.
              </p>
            </Accordion.Content>
            <Accordion.Title
              active={activeAccordion === 4}
              index={4}
              onClick={() => setActiveAccordion(4)}
            >
              <Icon name="dropdown" />
              Contact and Links
            </Accordion.Title>
            <Accordion.Content active={activeAccordion === 4}>
              <Header dividing as="h5">
                Assessor's Office
              </Header>

              <List>
                <List.Item icon="user" content="Matt Warner - Assessor" />
                <List.Item
                  icon="phone"
                  content={<a href="tel:1-319-753-8224">(319) 753-8224</a>}
                />
                <List.Item
                  icon="map marker"
                  content="513 N Main St, Burlington IA 52601"
                />
                <List.Item
                  icon="envelope"
                  content={
                    <a href="mailto:assessor@dmcounty.com">
                      assessor@dmcounty.com
                    </a>
                  }
                />
              </List>
              <Header dividing as="h5">
                GIS Department
              </Header>

              <List>
                <List.Item
                  icon="user"
                  content="Austin Roelfs - GIS Coordinator"
                />
                <List.Item
                  icon="phone"
                  content={<a href="tel:1-319-753-8759">(319) 753-8759</a>}
                />
                <List.Item
                  icon="map marker"
                  content="513 N Main St, Burlington IA 52601"
                />
                <List.Item
                  icon="envelope"
                  content={
                    <a href="mailto:gis@dmcounty.com">gis@dmcounty.com</a>
                  }
                />
              </List>
              <Header dividing as="h5">
                Links
              </Header>

              <Button.Group fluid vertical labeled icon>
                <Button
                  icon="external"
                  content="Des Moines County Website"
                  href="http://www.dmcounty.com/"
                />
                <Button
                  icon="external"
                  content="Burlington Website"
                  href="http://www.burlingtoniowa.org/"
                />
                <Button
                  icon="external"
                  content="West Burlington Website"
                  href="http://www.westburlington.org/"
                />
              </Button.Group>
            </Accordion.Content>
          </Accordion>
        </div>
      </div>
    </Sidebar>
  );
};

export default GISMappingApp;
